import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import DeviceService from "./DeviceService";
import type { SetupPackageRequest } from "./DeviceService";
import type {
    DeviceDetail,
    DevicePrintLog,
    UpdateDeviceSettingsRequest,
    PrintQueueJob,
} from "../generated/models/Device";

// -----------------------------
// DEVICES QUERIES
// -----------------------------

export const useDevicesQuery = () => {
    return useQuery<DeviceDetail[], Error>({
        queryKey: ["adminDevices"],
        queryFn: () => DeviceService.getAllDevices(),
    });
};

export const useDeviceByIdQuery = (id: number) => {
    return useQuery<DeviceDetail, Error>({
        queryKey: ["adminDevice", id],
        queryFn: () => DeviceService.getDeviceById(id),
        enabled: !!id,
    });
};

export const useDevicePrintLogsQuery = (id: number, limit = 100) => {
    return useQuery<DevicePrintLog[], Error>({
        queryKey: ["adminDevicePrintLogs", id, limit],
        queryFn: () => DeviceService.getDevicePrintLogs(id, limit),
        enabled: !!id,
    });
};

export const useDevicePrintQueueQuery = (id: number, limit = 50) => {
    return useQuery<PrintQueueJob[], Error>({
        queryKey: ["adminDevicePrintQueue", id, limit],
        queryFn: () => DeviceService.getDevicePrintQueue(id, limit),
        enabled: !!id,
    });
};

// -----------------------------
// DEVICES MUTATIONS
// -----------------------------

export const useUpdateDeviceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<DeviceDetail, Error, { id: number; data: UpdateDeviceSettingsRequest }>({
        mutationFn: ({ id, data }) => DeviceService.updateDevice(id, data),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["adminDevices"] });
            queryClient.invalidateQueries({ queryKey: ["adminDevice", id] });
            // Invalidate print logs cache as well (device settings might affect printing)
            queryClient.invalidateQueries({ queryKey: ["adminDevicePrintLogs", id] });
            showToast("Zařízení bylo úspěšně aktualizováno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat zařízení.", "error");
        },
    });
};

export const useDeleteDeviceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => DeviceService.deleteDevice(id),

        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ["adminDevices"] });
            // Remove device and print logs from cache when device is deleted
            queryClient.removeQueries({ queryKey: ["adminDevice", id] });
            queryClient.removeQueries({ queryKey: ["adminDevicePrintLogs", id] });
            queryClient.removeQueries({ queryKey: ["adminDevicePrintQueue", id] });
            showToast("Zařízení bylo úspěšně odstraněno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odstranit zařízení.", "error");
        },
    });
};

export const useTestDevicePrintMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, number>({
        mutationFn: (id) => DeviceService.testDevicePrint(id),

        onSuccess: (data, id) => {
            queryClient.invalidateQueries({ queryKey: ["adminDevicePrintQueue", id] });
            showToast(data.message || "Testovací tisk byl zařazen do fronty.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zařadit testovací tisk.", "error");
        },
    });
};

export const useRetryPrintMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, { deviceId: number; jobId: number }>({
        mutationFn: ({ deviceId, jobId }) => DeviceService.retryPrint(deviceId, jobId),

        onSuccess: (data, { deviceId }) => {
            queryClient.invalidateQueries({ queryKey: ["adminDevicePrintQueue", deviceId] });
            showToast(data.message || "Tisk bude opakován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zopakovat tisk.", "error");
        },
    });
};

// -----------------------------
// PREVIEW ALERT QUERY & MUTATIONS
// -----------------------------

export const usePreviewAlertStatusQuery = (id: number, enabled = true) => {
    return useQuery<{ active: boolean }, Error>({
        queryKey: ["previewAlertStatus", id],
        queryFn: () => DeviceService.getPreviewAlertStatus(id),
        enabled: !!id && enabled,
        refetchInterval: 5000, // Poll every 5 seconds to detect expiration
    });
};

export const useStartPreviewAlertMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<
        { success: boolean; message: string; durationSeconds?: number },
        Error,
        { id: number; durationSeconds?: number }
    >({
        mutationFn: ({ id, durationSeconds }) =>
            DeviceService.startPreviewAlert(id, durationSeconds),

        onSuccess: (data, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["previewAlertStatus", id] });
            showToast(data.message || "Vzorový poplach spuštěn.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se spustit vzorový poplach.", "error");
        },
    });
};

export const useStopPreviewAlertMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean; message: string }, Error, number>({
        mutationFn: (id) => DeviceService.stopPreviewAlert(id),

        onSuccess: (data, id) => {
            queryClient.invalidateQueries({ queryKey: ["previewAlertStatus", id] });
            showToast(data.message || "Vzorový poplach ukončen.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se ukončit vzorový poplach.", "error");
        },
    });
};

// -----------------------------
// SETUP PACKAGE
// -----------------------------

export const useDownloadSetupPackageMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, SetupPackageRequest>({
        mutationFn: async (data) => {
            const response = await DeviceService.downloadSetupPackage(data);
            // Stáhnout blob jako soubor
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `rpi-setup-${data.profile}-${data.hostname}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminDevices"] });
            showToast("Setup balík byl stažen", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout setup balík", "error");
        },
    });
};
